import {
  applyPhotoAdjustmentsToRgba,
  type PhotoAdjustments,
} from './shared/photo-adjustments.js';

const MAX_PREVIEW_EDGE = 1600;

export interface LocalPhotoPreview {
  dataUrl: string;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
}

function loadImage(sourceUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Die lokale Bildvorschau konnte nicht gelesen werden.'));
    image.src = sourceUrl;
  });
}

/** Uses the same pure pixel recipe as the server-side PNG export. */
export async function createLocalPhotoPreview(
  sourceUrl: string,
  adjustments: PhotoAdjustments,
): Promise<LocalPhotoPreview> {
  const image = await loadImage(sourceUrl);
  const scale = Math.min(1, MAX_PREVIEW_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Die lokale Bildvorschau ist in diesem Browser nicht verfügbar.');

  context.drawImage(image, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height);
  applyPhotoAdjustmentsToRgba(imageData.data, adjustments);
  context.putImageData(imageData, 0, 0);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width,
    height,
    sourceWidth: image.naturalWidth,
    sourceHeight: image.naturalHeight,
  };
}
